import { create } from 'zustand';
import type { CreatePasswordStep } from './createPassword.types';
import { FLOW_STEP } from './flowSteps';

export type { CreatePasswordStep } from './createPassword.types';

/**
 * Create Password State Interface
 */
interface CreatePasswordState {
  /** Current step in the create password flow */
  currentStep: CreatePasswordStep;
  /** Error message if any */
  error: string | null;
  /**
   * Whether the invitation / onboarding link was validated by the BFF.
   * The token that authorizes the next step is kept by the storage module
   * (in memory), never here.
   */
  isInvitationVerified: boolean;
  /** Loading state for API calls */
  isLoading: boolean;
}

/**
 * Create Password Actions Interface
 */
interface CreatePasswordActions {
  /** Set current step */
  setStep: (step: CreatePasswordStep) => void;
  /** Set error message and move to error step */
  setError: (error: string | null) => void;
  /** Set loading state */
  setLoading: (loading: boolean) => void;
  /** Handle successful invitation registration */
  invitationAccepted: () => void;
  /** Handle failed invitation registration */
  invitationFailed: (error: string) => void;
  /** Handle successful password creation */
  passwordCreated: () => void;
  /** Handle failed password creation */
  passwordFailed: (error: string) => void;
  /** Reset store to initial state */
  reset: () => void;
}

/**
 * Create Password Store Type
 */
export interface CreatePasswordStore
  extends CreatePasswordState, CreatePasswordActions {}

/**
 * Initial state for create password flow
 */
const initialState: CreatePasswordState = {
  currentStep: FLOW_STEP.loading,
  error: null,
  isInvitationVerified: false,
  isLoading: false,
};

/**
 * Create Password Store
 *
 * Manages state for the invitation-based password creation flow.
 *
 * ## Flow
 * 1. User arrives at /create-password with query params
 * 2. Store starts in 'loading' step
 * 3. On successful invitation: the onboarding token is kept in memory and
 *    the store moves to 'form' step
 * 4. On failed invitation: moves to 'error' step with error message
 * 5. On successful password creation: moves to 'success' step
 *
 * @example
 * ```tsx
 * const { currentStep, error, invitationAccepted } = useCreatePasswordStore();
 *
 * // After API success
 * invitationAccepted();
 * ```
 */
export const useCreatePasswordStore = create<CreatePasswordStore>()((set) => ({
  ...initialState,

  setStep: (step) => set({ currentStep: step }),

  setError: (errorMessage) =>
    set({
      error: errorMessage,
      currentStep: errorMessage ? FLOW_STEP.error : FLOW_STEP.form,
    }),

  setLoading: (loading) => set({ isLoading: loading }),

  invitationAccepted: () =>
    set({
      isInvitationVerified: true,
      currentStep: FLOW_STEP.form,
      error: null,
      isLoading: false,
    }),

  invitationFailed: (error) =>
    set({
      error,
      currentStep: FLOW_STEP.error,
      isInvitationVerified: false,
      isLoading: false,
    }),

  passwordCreated: () =>
    set({
      currentStep: FLOW_STEP.success,
      error: null,
      isLoading: false,
    }),

  passwordFailed: (error) =>
    set({
      error,
      isLoading: false,
      // Stay on form step to allow retry
    }),

  reset: () => set(initialState),
}));
